import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ChevronDown, LogOut, Info, Film, Play, Menu, X, Tv, Calendar, BookOpen, Users, Globe } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_90b4c8a6-2372-4e9e-bd52-ac51039d9d6b/artifacts/l6uwsqcm_logo_bravo_cinemastreaming.png";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const iconMap = {
  Info, Film, Play, Tv, Calendar, BookOpen, Users, Globe, Search, Menu
};

const Header = ({ onOpenAuth, onOpenSearch }) => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [cinemaSession, setCinemaSession] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [menus, setMenus] = useState([]);
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRefs = useRef({});

  const defaultMenus = [
    { id: 'default-1', label: 'SABER MÁS', icon: 'Info', order: 0, is_active: true, has_dropdown: true, items: [
      { id: 'd1', label: 'Sobre nosotros', path: '/sobre-nosotros', is_active: true },
      { id: 'd2', label: 'Dispositivo Bravo', path: '/conoce-bravo', is_active: true },
      { id: 'd3', label: 'Únete', path: '/colabora', is_active: true },
      { id: 'd4', label: 'Condiciones de contratación', path: '/condiciones', is_active: true },
      { id: 'd5', label: 'Preguntas frecuentes', path: '/faq', is_active: true }
    ]},
    { id: 'default-2', label: 'CONTENIDOS', icon: 'Film', order: 1, is_active: true, has_dropdown: true, items: [
      { id: 'd6', label: 'Catálogo', path: '/catalogo', is_active: true },
      { id: 'd7', label: 'Calendario de estrenos', path: '/calendario', is_active: true },
      { id: 'd8', label: 'Eventos pasados', path: '/eventos-pasados', is_active: true },
      { id: 'd9', label: 'Turandot Les Arts', path: '/turandot-inscripciones', is_active: true }
    ]},
    { id: 'default-3', label: 'BRAVO', icon: 'Play', order: 2, is_active: true, has_dropdown: true, items: [
      { id: 'd10', label: 'Dispositivo Bravo', path: '/dispositivo-bravo', is_active: true },
      { id: 'd11', label: 'Distribución DCP', path: '/dispositivo-bravo-dcp', is_active: true }
    ]}
  ];

  const activeMenus = menus.length > 0 ? menus : defaultMenus;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 100);
    window.addEventListener('scroll', handleScroll);
    const session = localStorage.getItem('cinema_session');
    if (session) setCinemaSession(JSON.parse(session));
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/navigation-menus/active`)
      .then(res => res.json())
      .then(data => setMenus(data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutside = Object.values(dropdownRefs.current).every(
        ref => !ref || !ref.contains(event.target)
      );
      if (isOutside) setOpenDropdown(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('cinema_session');
    setCinemaSession(null);
    navigate('/');
  };

  const handleNavClick = () => {
    window.scrollTo(0, 0);
    setOpenDropdown(null);
    setMobileMenuOpen(false);
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) setMobileMenuOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-[#0a0a0a]/95 backdrop-blur-sm shadow-lg' : 'bg-gradient-to-b from-black/80 to-transparent'
      }`}>
        <div className="max-w-[1920px] mx-auto px-4 md:px-12 py-4 flex items-center justify-between">
          <Link to="/" onClick={handleNavClick} className="flex items-center gap-3 group">
            <img src={LOGO_URL} alt="Bravo Cinemastreaming" className="h-10 md:h-12 w-auto" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-4 md:gap-6">
            {activeMenus.map((menu) => {
              const IconComp = iconMap[menu.icon] || Info;
              
              if (menu.has_dropdown && menu.items && menu.items.length > 0) {
                return (
                  <div key={menu.id} className="relative" ref={el => dropdownRefs.current[menu.id] = el}>
                    <button
                      onClick={() => setOpenDropdown(openDropdown === menu.id ? null : menu.id)}
                      className="flex items-center gap-1.5 text-white text-sm font-medium tracking-wide hover:text-[#e4002b] transition-colors"
                      data-testid={`menu-${menu.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <IconComp className="w-4 h-4" />
                      {menu.label}
                      <ChevronDown className={`w-4 h-4 transition-transform ${openDropdown === menu.id ? 'rotate-180' : ''}`} />
                    </button>
                    {openDropdown === menu.id && (
                      <div className="absolute top-full left-0 mt-2 w-56 bg-[#1a1a1a] rounded-lg overflow-hidden shadow-xl border border-white/10">
                        {menu.items.map((item) => (
                          <Link
                            key={item.id}
                            to={item.path}
                            onClick={handleNavClick}
                            className="block px-4 py-3 text-sm text-white/80 hover:bg-[#e4002b] hover:text-white transition-colors"
                          >
                            {item.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              } else {
                return (
                  <Link
                    key={menu.id}
                    to={menu.path || '/'}
                    onClick={handleNavClick}
                    className="flex items-center gap-1.5 text-white text-sm font-medium tracking-wide hover:text-[#e4002b] transition-colors group"
                    data-testid={`menu-${menu.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {menu.icon === 'Play' ? (
                      <div className="w-5 h-5 bg-[#e4002b] rounded flex items-center justify-center group-hover:bg-[#c70024] transition-colors">
                        <Play className="w-3 h-3 text-white fill-white" />
                      </div>
                    ) : (
                      <IconComp className="w-4 h-4" />
                    )}
                    {menu.label}
                  </Link>
                );
              }
            })}

            <button
              onClick={onOpenSearch}
              className="w-10 h-10 rounded-full border border-white/30 flex items-center justify-center text-white hover:border-white hover:bg-white/10 transition-all"
              data-testid="search-button"
            >
              <Search className="w-4 h-4" />
            </button>

            {cinemaSession ? (
              <div className="flex items-center gap-3">
                <Link
                  to="/cinema/booking"
                  onClick={handleNavClick}
                  className="px-4 py-2.5 bg-[#e4002b] text-white text-sm font-medium tracking-wide rounded hover:bg-[#c70024] transition-all"
                >
                  {cinemaSession.venue_name}
                </Link>
                <button
                  onClick={handleLogout}
                  className="p-2.5 border border-white/30 text-white rounded hover:bg-white/10 transition-all"
                  title="Cerrar sesión"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <Link
                to="/cinema/login"
                onClick={handleNavClick}
                className="px-5 py-2.5 border border-white text-white text-sm font-medium tracking-wide rounded hover:bg-white hover:text-black transition-all"
                data-testid="registro-acceso-btn"
              >
                REGISTRO / ACCESO CINES
              </Link>
            )}
          </nav>

          {/* Mobile Navigation Controls */}
          <div className="flex md:hidden items-center gap-3">
            <button
              onClick={onOpenSearch}
              className="w-10 h-10 rounded-full border border-white/30 flex items-center justify-center text-white hover:border-white hover:bg-white/10 transition-all"
              data-testid="search-button-mobile"
            >
              <Search className="w-4 h-4" />
            </button>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="w-10 h-10 rounded-full border border-white/30 flex items-center justify-center text-white hover:border-white hover:bg-white/10 transition-all"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)} />
          <div className="absolute top-[72px] left-0 right-0 bg-[#0a0a0a] border-t border-white/10 max-h-[calc(100vh-72px)] overflow-y-auto">
            <nav className="p-4 space-y-2">
              {activeMenus.map((menu) => {
                const IconComp = iconMap[menu.icon] || Info;
                if (menu.has_dropdown && menu.items && menu.items.length > 0) {
                  return (
                    <div key={menu.id} className="border-b border-white/10 pb-4">
                      <p className="text-white/50 text-xs uppercase tracking-wider mb-2 px-3">{menu.label}</p>
                      {menu.items.map((item) => (
                        <Link
                          key={item.id}
                          to={item.path}
                          onClick={handleNavClick}
                          className="flex items-center gap-3 px-3 py-3 text-white hover:bg-white/10 rounded-lg transition-colors"
                        >
                          <IconComp className="w-5 h-5 text-[#e4002b]" />
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  );
                } else {
                  return (
                    <Link
                      key={menu.id}
                      to={menu.path || '/'}
                      onClick={handleNavClick}
                      className="flex items-center gap-3 px-3 py-3 text-white hover:bg-white/10 rounded-lg transition-colors"
                    >
                      {menu.icon === 'Play' ? (
                        <div className="w-5 h-5 bg-[#e4002b] rounded flex items-center justify-center">
                          <Play className="w-3 h-3 text-white fill-white" />
                        </div>
                      ) : (
                        <IconComp className="w-5 h-5 text-[#e4002b]" />
                      )}
                      <span className="font-medium">{menu.label}</span>
                    </Link>
                  );
                }
              })}

              <div className="pt-4">
                {cinemaSession ? (
                  <div className="space-y-3">
                    <Link
                      to="/cinema/booking"
                      onClick={handleNavClick}
                      className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-[#e4002b] text-white font-medium rounded-lg hover:bg-[#c70024] transition-all"
                    >
                      {cinemaSession.venue_name}
                    </Link>
                    <button
                      onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                      className="flex items-center justify-center gap-2 w-full px-4 py-3 border border-white/30 text-white rounded-lg hover:bg-white/10 transition-all"
                    >
                      <LogOut className="w-4 h-4" />
                      Cerrar sesión
                    </button>
                  </div>
                ) : (
                  <Link
                    to="/cinema/login"
                    onClick={handleNavClick}
                    className="flex items-center justify-center w-full px-4 py-3 bg-[#e4002b] text-white font-medium rounded-lg hover:bg-[#c70024] transition-all"
                    data-testid="registro-acceso-btn-mobile"
                  >
                    REGISTRO / ACCESO CINES
                  </Link>
                )}
              </div>
            </nav>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
